import { faCheck, faExclamationTriangle, faPencilAlt, faTimes } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import dayjs from 'dayjs'
import numeral from 'numeral'
import React, { useEffect } from 'react'
import { Button, Card, Col, Row } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { Link, useHistory, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { AppDispatch } from '../store'
import { deleteApplication, fetchApplication } from '../store/applicationSlice'
import { RootState } from '../store/rootReducer'

const Application = (): React.ReactElement | null => {
  const history = useHistory()
  const { t } = useTranslation(['application', 'common'])
  const { id } = useParams<{ id: string }>()
  const dispatch = useDispatch<AppDispatch>()
  const { application, loading, error } = useSelector((state: RootState) => state.application)

  useEffect(() => {
    dispatch(fetchApplication(id))
  }, [dispatch, id])

  const onDelete = async () => {
    // eslint-disable-next-line no-restricted-globals
    const conf = confirm(t('viltuEyda'))
    if (conf) {
      try {
        await dispatch(deleteApplication(id))
        toast.success(t('eytt'))
        history.push('/umsoknir')
      } catch (e) {
        toast.error(t('common:kerfisvilla'))
      }
    }
  }

  if (loading === 'pending') {
    // TODO: Loader...
    return null
  }

  if (error || !application) {
    // TODO: Upp kom óvænt villa...
    return null
  }

  return (
    <Card>
      <Card.Header>
        <Row>
          <Col>
            <h4 className="mt-1">{t('upplysingar')}</h4>
          </Col>
          <Col>
            {application.status === 'NEW' && (
              <>
                <Button variant="danger" className="float-right" onClick={onDelete}>
                  <FontAwesomeIcon icon={faExclamationTriangle} /> {t('eyda')}
                </Button>

                <Link
                  className="btn btn-primary float-right mr-2"
                  style={{ color: '#fff', display: 'inline-block' }}
                  to={`/breytaumsokn/${application.id}`}
                >
                  <FontAwesomeIcon icon={faPencilAlt} /> {t('breyta')}
                </Link>
              </>
            )}
          </Col>
        </Row>
      </Card.Header>
      <Card.Body>
        <h5 style={{ fontWeight: 100 }}>{t('thatttakandi')}</h5>
        <Row>
          <Col>
            <strong>{t('nafn')}</strong>
            <div>{application.name}</div>
          </Col>
          <Col>
            <strong>{t('kennitala')}</strong>
            <div>{application.ssn}</div>
          </Col>
        </Row>
        <Row className="mt-4">
          <Col>
            <strong>{t('timabil')}</strong>
            <div>
              <div>{application.period.name}</div>
              {application.period_parts && application.period_parts.length > 0 ? (
                application.period_parts.map((part) => (
                  <div key={part.id}>
                    <i>
                      {dayjs(part.period_part.date_from).format('DD.MM.YYYY')} -{' '}
                      {dayjs(part.period_part.date_to).format('DD.MM.YYYY')}
                    </i>
                  </div>
                ))
              ) : (
                <i>
                  {dayjs(application.period.date_from).format('DD.MM.YYYY')} -{' '}
                  {dayjs(application.period.date_to).format('DD.MM.YYYY')}
                </i>
              )}
              {application.period_max_hours && (
                <div>
                  {t('periodMaxHours', {
                    hours: numeral(application.period_max_hours).format('0,0.[00]'),
                  })}
                </div>
              )}
            </div>
          </Col>
          <Col>
            <strong>{t('oskUmVinnustad')}</strong>
            {application.workplaces.map((workplace) => (
              <div key={workplace.id}>
                <div className="d-inline-block">{workplace.name}</div>
              </div>
            ))}
          </Col>
        </Row>
        {application.period.apply_info && (
          <Row className="mt-4">
            <Col>
              <strong>{t('upplysingarUmsaekjanda')}</strong>
              <div>{application.period.apply_info}</div>
            </Col>
          </Row>
        )}
        <Row className="mt-4">
          <Col>
            <strong>{t('netfang')}</strong>
            <div>{application.email}</div>
          </Col>
          <Col>
            <strong>{t('farsimi')}</strong>
            <div>{application.tel}</div>
          </Col>
        </Row>
        <Row className="mt-4">
          <Col>
            <strong>{t('bankareikningur')}</strong>
            <div>{application.account}</div>
          </Col>
          <Col>
            <strong>{t('personuafslattur')}</strong>
            <div>
              {application.use_personal_allowance ? (
                <FontAwesomeIcon icon={faCheck} className="text-success" />
              ) : (
                <FontAwesomeIcon icon={faTimes} className="text-muted" />
              )}
            </div>
          </Col>
        </Row>
        <Row className="mt-4">
          <Col>
            <strong>{t('sertharfir')}</strong>
            <div>
              {application.has_special_needs ? (
                <div className="d-flex align-items-start">
                  <i className="fa fa-medkit text-danger mr-2 mt-1"></i>
                  <em className="text-muted">{application.special_needs || t('engarAthugasemdirSkradar')}</em>
                </div>
              ) : (
                <em className="text-muted">{t('engarSertharfir')}</em>
              )}
            </div>
          </Col>
          <Col>
            <strong>{t('annad')}</strong>
            <div>{application.other || ' '}</div>
          </Col>
        </Row>
        {/* <Row className="mt-4">
          <Col>
            <strong>{t('annadStarfsstadur')}</strong>
            <div>{application.other_workplace || ' '}</div>
          </Col>
        </Row> */}
        <br />
        <h5 style={{ fontWeight: 100 }}>{t('adstandendur')}</h5>
        {application.contacts.map((contact) => (
          <div key={contact.id}>
            <Row className="mt-4">
              <Col>
                <strong>{t('nafn')}</strong>
                <div>{contact.name}</div>
              </Col>
              <Col>
                <strong>{t('kennitala')}</strong>
                <div>{contact.ssn}</div>
              </Col>
              <Col>
                <strong>{t('netfang')}</strong>
                <div>{contact.email}</div>
              </Col>
              <Col>
                <strong>{t('farsimi')}</strong>
                <div>{contact.tel}</div>
              </Col>
            </Row>
          </div>
        ))}
      </Card.Body>
    </Card>
  )
}

export default Application
